import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import usePageInteractions from './hooks/usePageInteractions';
import { CMSProvider } from './context/CMSContext';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import LinkInterceptor from './components/LinkInterceptor';
import InteractiveCursor from './components/InteractiveCursor';

// Lazy-loaded Pages
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Team = lazy(() => import('./pages/Team'));
const Blog = lazy(() => import('./pages/Blog'));
const Contact = lazy(() => import('./pages/Contact'));
const Terms = lazy(() => import('./pages/Terms'));

// Lazy-loaded Service detail pages
const ServiceBranding = lazy(() => import('./pages/ServiceBranding'));
const ServiceSocialMedia = lazy(() => import('./pages/ServiceSocialMedia'));
const ServiceWebDevelopment = lazy(() => import('./pages/ServiceWebDevelopment'));
const Service3DEventDesign = lazy(() => import('./pages/Service3DEventDesign'));
const ServiceVideoAnimation = lazy(() => import('./pages/ServiceVideoAnimation'));
const ServiceSEO = lazy(() => import('./pages/ServiceSEO'));

// Lazy-loaded Portfolio pages
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Portfolio3DEventExhibition = lazy(() => import('./pages/Portfolio3DEventExhibition'));
const PortfolioWebDevelopment = lazy(() => import('./pages/PortfolioWebDevelopment'));
const PortfolioBranding = lazy(() => import('./pages/PortfolioBranding'));
const PortfolioVideoAnimation = lazy(() => import('./pages/PortfolioVideoAnimation'));
const PortfolioSocialMediaMarketing = lazy(() => import('./pages/PortfolioSocialMediaMarketing'));
const PortfolioSEO = lazy(() => import('./pages/PortfolioSEO'));

// Lazy-loaded Blog post pages
const BlogPostDetail = lazy(() => import('./pages/BlogPostDetail'));

function PageRouteWrapper() {
  usePageInteractions();
  return <Outlet />;
}

function AppContent() {
  return (
    <>
      <ScrollToTop />
      <LinkInterceptor />
      <InteractiveCursor />

      <Header />

      <Suspense fallback={
        <div style={{
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          color: 'rgba(255, 255, 255, 0.4)',
          fontSize: '11px',
          fontWeight: 900,
          letterSpacing: '0.24em',
          textTransform: 'uppercase'
        }}>
          <div className="lazy-spinner" style={{
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            border: '2px solid rgba(105, 200, 217, 0.1)',
            borderTopColor: '#69c8d9',
            animation: 'lazySpin 0.8s linear infinite'
          }} />
          <style>{`
            @keyframes lazySpin { to { transform: rotate(360deg); } }
          `}</style>
          Loading
        </div>
      }>
        <Routes>
          <Route element={<PageRouteWrapper />}>
            {/* Clean and Legacy Main Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/index.html" element={<Home />} />

            <Route path="/about" element={<About />} />
            <Route path="/about.html" element={<About />} />

            <Route path="/services" element={<Services />} />
            <Route path="/services.html" element={<Services />} />

            <Route path="/team" element={<Team />} />
            <Route path="/team.html" element={<Team />} />

            <Route path="/blog" element={<Blog />} />
            <Route path="/blog.html" element={<Blog />} />

            <Route path="/contact" element={<Contact />} />
            <Route path="/contact.html" element={<Contact />} />

            <Route path="/terms" element={<Terms />} />
            <Route path="/terms.html" element={<Terms />} />

            {/* Clean and Legacy Service routes */}
            <Route path="/services/branding" element={<ServiceBranding />} />
            <Route path="/service-branding.html" element={<ServiceBranding />} />

            <Route path="/services/digital-marketing" element={<ServiceSocialMedia />} />
            <Route path="/service-social-media.html" element={<ServiceSocialMedia />} />

            <Route path="/services/web-development" element={<ServiceWebDevelopment />} />
            <Route path="/service-web-development.html" element={<ServiceWebDevelopment />} />

            <Route path="/services/3d-event-design" element={<Service3DEventDesign />} />
            <Route path="/service-3d-event-design.html" element={<Service3DEventDesign />} />

            <Route path="/services/video-animation" element={<ServiceVideoAnimation />} />
            <Route path="/service-video-animation.html" element={<ServiceVideoAnimation />} />

            <Route path="/services/seo-ai-solutions" element={<ServiceSEO />} />
            <Route path="/service-seo.html" element={<ServiceSEO />} />

            {/* Portfolio routes */}
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/portfolio.html" element={<Portfolio />} />
            <Route path="/portfolio/3d-event-exhibition" element={<Portfolio3DEventExhibition />} />
            <Route path="/portfolio/web-development" element={<PortfolioWebDevelopment />} />
            <Route path="/portfolio/branding" element={<PortfolioBranding />} />
            <Route path="/portfolio/video-animation" element={<PortfolioVideoAnimation />} />
            <Route path="/portfolio/social-media-marketing" element={<PortfolioSocialMediaMarketing />} />
            <Route path="/portfolio/seo" element={<PortfolioSEO />} />

            {/* Dynamic and Legacy Blog routes */}
            <Route path="/blog/:id" element={<BlogPostDetail />} />
            <Route path="/blog-:id.html" element={<BlogPostDetail />} />
          </Route>
        </Routes>
      </Suspense>

      <Footer />
    </>
  );
}

export default function App() {
  return (
    <Router>
      <CMSProvider>
        <AppContent />
      </CMSProvider>
    </Router>
  );
}
